import { createPlugin } from '@customerio/dryad';
import { ATTRIBUTE_CONDITION_TYPE } from '../../codecs/attribute-condition';
import {
  isSupportedAttributeFilter,
  parseAttributeFilter,
  serializeAttributeFilter,
  validateAttributeFilter,
  type AttributeFilterState,
  type GroupableFamily,
} from '../../codecs/attribute-filter';
import { operatorsInFamily } from '../../operators';

/**
 * Equality + existence only, and not as UI taste: people-search executes on
 * the bitmap index path, where the backend serves only equals/exists (plus
 * email_valid and one_of) directly — anything else parses and then fails the
 * query with a 400 telling the user to wrap the condition in a segment.
 */
export const PEOPLE_SEARCH_OPERATOR_FAMILIES = ['equality', 'existence'] as const satisfies readonly GroupableFamily[];

export function defaultOperatorFor(families: readonly GroupableFamily[]): string {
  const [first] = families;
  return (first == null ? undefined : operatorsInFamily(first)[0]?.value) ?? 'eq';
}

/**
 * The `{ attribute: {...} }` leaf. Gated on this surface's declared families —
 * a plugin must not claim an operator its row's dropdown can't offer. A saved
 * filter carrying an unservable operator stays on the error-node passthrough
 * (dryad's fallback for unclaimed nodes, re-serialized byte-for-byte), which
 * renders as a remove-only row; since the backend rejects the filter at query
 * time either way, remove-and-rebuild is the only edit that helps.
 */
export function createPeopleFilterAttributePlugin(families: readonly GroupableFamily[]) {
  const operator = defaultOperatorFor(families);
  return createPlugin({
    type: ATTRIBUTE_CONDITION_TYPE,
    label: 'Attribute',
    parse: (json: unknown) => (isSupportedAttributeFilter(json, families) ? parseAttributeFilter(json) : null),
    serialize: serializeAttributeFilter,
    validate: validateAttributeFilter,
    createDefault: (): AttributeFilterState => ({ field: '', operator, value: null, value2: null }),
  });
}

export const peopleFilterAttributePlugin = createPeopleFilterAttributePlugin(PEOPLE_SEARCH_OPERATOR_FAMILIES);
